/** The frets the board shows: the whole neck, or a window onto one position. */
export interface FretWindow {
  startFret: number;
  endFret: number;
}

export const HIGHEST_FRET = 15;

// A window narrower than this can't hold a scale position, so the picker never offers one.
export const MIN_WINDOW_FRETS = 4;

export const FULL_NECK: FretWindow = { startFret: 1, endFret: HIGHEST_FRET };

export function isFretWindow(value: unknown): value is FretWindow {
  if (typeof value !== "object" || value === null) return false;
  const { startFret, endFret } = value as Record<string, unknown>;
  return (
    Number.isInteger(startFret) &&
    Number.isInteger(endFret) &&
    (startFret as number) >= 1 &&
    (endFret as number) <= HIGHEST_FRET &&
    (endFret as number) - (startFret as number) + 1 >= MIN_WINDOW_FRETS
  );
}

export function isFullNeck({ startFret, endFret }: FretWindow): boolean {
  return startFret === FULL_NECK.startFret && endFret === FULL_NECK.endFret;
}

/**
 * The window a two-thumb range picker lands on. When the thumbs come closer than the minimum
 * width, the thumb that didn't move gives way, so dragging never snaps back.
 */
export function windowFromThumbs(
  startFret: number,
  endFret: number,
  movedThumb: "start" | "end"
): FretWindow {
  if (endFret - startFret + 1 >= MIN_WINDOW_FRETS) return { startFret, endFret };
  if (movedThumb === "start") {
    const start = Math.min(startFret, HIGHEST_FRET - MIN_WINDOW_FRETS + 1);
    return { startFret: start, endFret: start + MIN_WINDOW_FRETS - 1 };
  }
  const end = Math.max(endFret, MIN_WINDOW_FRETS);
  return { startFret: end - MIN_WINDOW_FRETS + 1, endFret: end };
}

export function describeFretWindow(window: FretWindow): string {
  return isFullNeck(window) ? "Full neck" : `Frets ${window.startFret}–${window.endFret}`;
}
